import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, Observable, of, shareReplay, take } from 'rxjs';
import { SeminarDateSeriesListModel } from '../../../../models/seminar.model';
import { TableHeaderInterfaceModel } from '../../../../models/table.model';
import { TrainingCategoryModel } from '../../../../models/training-category.model';
import { SeminarService } from '../../../../services/seminar.service';
import { TrainingCategoryService } from '../../../../services/training-category.service';
import {
  getSeminarDates,
  getSeminarDateString,
  getSeminarFobiPoints,
  getSeminarLecturerText,
  getSeminarLocationString,
  getSeminarOccupancyColor,
  getSeminarStatus,
  getSeminarStatusChipClass,
  getSeminarStatusChipText,
} from '../../../../utils/seminar.utils';
import {
  Filter,
  TableColumn,
  TableHeaderButton,
} from '../../../shared-components/custom-table/custom-table.component';

@Component({
  selector: 'app-seminars',
  templateUrl: './seminars.component.html',
  styleUrl: './seminars.component.scss',
  standalone: false,
})
export class SeminarsComponent {
  public dataSource$: Observable<SeminarDateSeriesListModel[]>;
  public loadingSeminars = false;
  public cardSlides: TableHeaderInterfaceModel[] = [];
  private seminarCategories: TrainingCategoryModel[];
  private seminarDateSeriesList: SeminarDateSeriesListModel[];
  private selectedCategoryId?: number;

  public tableHeaderButton: TableHeaderButton = {
    label: 'Seminar erstellen',
    icon: 'fa-plus',
    onClick: () => {
      this.router.navigate(['create'], { relativeTo: this.activatedRoute });
    },
  };

  constructor(
    private seminarService: SeminarService,
    private courseTypeService: TrainingCategoryService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
      this.selectedCategoryId = params['categoryId']
        ? Number(params['categoryId'])
        : undefined;
    });
    this.fetchSeminarCategories();
    this.getSeminars();
  }

  private getSeminars() {
    this.loadingSeminars = true;
    this.dataSource$ = this.seminarService.getSeminars(true, true).pipe(
      take(1),
      map(seminarDateSeriesList => {
        this.loadingSeminars = false;
        this.seminarDateSeriesList = seminarDateSeriesList;
        return this.getVisibleSeminars().map(seminar =>
          this.mapToTableData(seminar)
        );
      }),
      catchError(error => {
        this.loadingSeminars = false;
        console.error('Error fetching seminars', error);
        return [];
      }),
      shareReplay(1) // Cache the result
    );
  }

  private fetchSeminarCategories() {
    this.courseTypeService
      .getAllTrainingCategories()
      .pipe(take(1))
      .subscribe({
        next: seminarCategories => {
          this.seminarCategories = seminarCategories;
          this.cardSlides = seminarCategories.map(category => ({
            title: category.name,
            details: this.getSeminarsCountByCategory(category.id).pipe(
              map(count =>
                count == 1 ? `${count} Seminar` : `${count} Seminare`
              )
            ),
            color50: category.color50,
            color0: category.color0,
            color70: category.color70,
            color80: category.color80,
            color90: category.color90,
            selected: category.id === this.selectedCategoryId,
          }));

          this.cardSlides.unshift({
            title: 'Alle Seminare',
            details: this.dataSource$.pipe(
              map(seminars => `${seminars.length} Seminare`)
            ),
            color50: '#0E332E',
            color0: '#0E332E',
            color70: '#000000',
            color80: '#E8F0EF',
            color90: '#E8F0EF',
            selected: !this.selectedCategoryId,
          });
        },
      });
  }

  private getSeminarsCountByCategory(
    seminarCategoryId: number
  ): Observable<number> {
    return this.dataSource$.pipe(
      map(
        seminars =>
          seminars.filter(
            seminar =>
              seminar.category1?.id === seminarCategoryId ||
              seminar.category2?.id === seminarCategoryId
          ).length
      ),
      catchError(error => {
        console.error(
          'Error fetching seminars count by category from dataSource',
          error
        );
        return of(0);
      })
    );
  }

  private filterSeminarsByCategory(typeId?: number) {
    this.selectedCategoryId = typeId;
    this.applySeminarVisibilityFilters();
    this.updateQueryParams();
  }

  private getVisibleSeminars(): SeminarDateSeriesListModel[] {
    let visibleSeminars = [...(this.seminarDateSeriesList ?? [])];

    if (!this.selectedCategoryId) {
      return visibleSeminars;
    }

    return visibleSeminars.filter(
      seminarDateSeries =>
        seminarDateSeries.category1?.id === this.selectedCategoryId ||
        seminarDateSeries.category2?.id === this.selectedCategoryId
    );
  }

  private applySeminarVisibilityFilters() {
    this.dataSource$ = of(
      this.getVisibleSeminars().map(seminarDateSeries =>
        this.mapToTableData(seminarDateSeries)
      )
    );
  }

  private updateQueryParams() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        categoryId: this.selectedCategoryId ?? null,
      },
      queryParamsHandling: 'merge',
    });
  }

  private mapToTableData(seminarDateSeries: SeminarDateSeriesListModel) {
    return {
      ...seminarDateSeries,
      title: seminarDateSeries.seminar.title,
      lecturer: getSeminarLecturerText(seminarDateSeries),
      fobiPoints: getSeminarFobiPoints(seminarDateSeries),
      occupancy: `${seminarDateSeries.participantCount}/${seminarDateSeries.seminar.maxParticipants ?? '0'}`,
      status: getSeminarStatus(seminarDateSeries),
      isPastSeminar: getSeminarStatus(seminarDateSeries) === 'finished',
    };
  }

  public columns: TableColumn[] = [
    {
      columnDef: 'occupancy',
      header: 'Belegte Plätze',
      width: '10%',
      render: row => {
        const seminarStatusColor = getSeminarOccupancyColor(row);
        const statusClass = `inwi-chip-text-${seminarStatusColor}-outline`;

        return `<div class="${statusClass}">
        ${row.occupancy}
      </div>`;
      },
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'title',
      header: 'Inhalt',
      width: '25%',
      sortable: true,
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'dates',
      header: 'Datum',
      width: '15%',
      filterValue: row => getSeminarDates(row),
      render: row => {
        return getSeminarDateString(row) ?? null;
      },
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'lecturer',
      header: 'Dozierende',
      width: '15%',
      fontWeight: '400',
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'fobiPoints',
      header: 'FP',
      headerAlign: 'center',
      cellAlign: 'center',
      width: '10%',
      fontWeight: '400',
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'modality',
      header: 'Ort',
      width: '15%',
      fontWeight: '400',
      tooltip: row => ({
        tooltipText:
          row.seminar?.modality === 'online'
            ? ''
            : getSeminarLocationString(row, true),
        position: 'top',
        autoHide: false,
      }),
      filterValue: row =>
        row.seminar?.modality === 'online'
          ? 'online'
          : getSeminarLocationString(row, false, true),
      render: row => {
        if (!row.seminar?.modality || row.seminar?.modality === '') {
          return null;
        }
        if (row.seminar?.modality === 'online') {
          return `<div class="flex-r gap-4 align-center"><i class="p-5 fa-regular fa-globe"></i> Online</div>`;
        }
        return `<div class="flex-r gap-4 align-center"><i class="p-5 fa-regular fa-map-marker-alt"></i> ${getSeminarLocationString(row, false, true)}</div>`;
      },
    },
    {
      columnDef: 'status',
      header: 'Status',
      width: '10%',
      filterValue: row => getSeminarStatusChipText(row.status),
      render: row => {
        const statusClass = getSeminarStatusChipClass(row.status);

        return `<div class="${statusClass}">
        ${getSeminarStatusChipText(row.status)}
      </div>`;
      },
    },
    {
      columnDef: 'actions',
      header: 'Aktionen',
      width: '6%',
      headerAlign: 'center',
      cellAlign: 'center',
      fontWeight: '400',
      tooltip: () => ({
        tooltipText: 'Seminar duplizieren',
        position: 'top',
        autoHide: true,
      }),
      render: () =>
        `<div class="action-buttons"><button type="button" class="inwi-icon-button-small-secondary" pTooltip="Umbenennen" tooltipPosition="top"><i class="fa-regular fa-copy" aria-hidden="true"></i></button></div>`,
      onCellClick: (row: SeminarDateSeriesListModel) =>
        this.onDuplicateSeminar(row),
    },
  ];

  // Define filter configuration
  public filters: Filter[] = [
    {
      filteredColumn: 'isPastSeminar',
      type: 'checkbox',
      value: false,
      label: 'Vergangene Seminare anzeigen',
    },
    {
      filteredColumn: 'dates',
      type: 'daterange',
    },
    {
      filteredColumn: 'status',
      type: 'multiselect',
      selectOptions: [
        {
          value: 'online',
          label: 'Online',
        },
        {
          value: 'finished',
          label: 'Vorbei',
        },
        {
          value: 'waitlist',
          label: 'Warteliste',
        },
        {
          value: 'draft',
          label: 'Entwurf',
        },
        {
          value: 'canceled',
          label: 'Abgesagt',
        },
      ],
      placeholder: 'Status auswählen...',
      label: 'Status',
    },
    {
      filteredColumn: 'seminar.modality',
      type: 'select',
      selectOptions: [
        {
          value: 'online',
          label: 'Online',
        },
        {
          value: 'offline',
          label: 'Vor Ort',
        },
      ],
      placeholder: 'Ort auswählen...',
      label: 'Ort',
    },
  ];

  // Define the function that will be passed to the table component
  public onDuplicateSeminar(row: SeminarDateSeriesListModel) {
    this.router.navigate(['create'], {
      relativeTo: this.activatedRoute,
      queryParams: { duplicateFrom: btoa(String(row.seminar.id)) },
    });
  }

  public onCellClick(row: SeminarDateSeriesListModel, column: any) {
    if (row.seminar.isDraft) {
      this.router.navigate(['edit', btoa(String(row.seminar.id))], {
        relativeTo: this.activatedRoute,
      });
      return;
    }
    this.router.navigate(
      [btoa(String(row.seminar.id)), btoa(String(row.dateSeriesId))],
      {
        relativeTo: this.activatedRoute,
      }
    );
  }

  public onCardClicked(event: TableHeaderInterfaceModel) {
    this.cardSlides = this.cardSlides.map(slide => ({
      ...slide,
      selected: slide.title === event.title,
    }));

    const selectedType = this.seminarCategories.find(
      type => type.name === event.title
    );

    if (event.title === 'Alle Seminare') {
      this.filterSeminarsByCategory();
    } else {
      this.filterSeminarsByCategory(selectedType.id);
    }
  }
}
